"use client";
import Cookie from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { GET_TOKEN_FAILURE_COUNT_KEY, GET_KMSI_FAILURE_MAX_RETRY_COUNT, AUTH_TOKEN_KEY, GET_TOKEN_FAILURE_MAX_RETRY_COUNT, GET_KMSI_FAILURE_COUNT_KEY } from "./constants.js";
const getTokenFailureCount = {
  failureCountTimeout: setTimeout(() => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem(GET_TOKEN_FAILURE_COUNT_KEY, "0");
    }
  }, 14e3),
  get() {
    const counterValue = sessionStorage.getItem(GET_TOKEN_FAILURE_COUNT_KEY) || "";
    return Number.parseInt(counterValue) || 0;
  },
  increment() {
    const currentValue = this.get();
    if (this.failureCountTimeout) {
      clearTimeout(this.failureCountTimeout);
    }
    sessionStorage.setItem(
      GET_TOKEN_FAILURE_COUNT_KEY,
      (currentValue + 1).toString()
    );
    this.failureCountTimeout = setTimeout(() => {
      sessionStorage.setItem(GET_TOKEN_FAILURE_COUNT_KEY, "0");
    }, 14e3);
  },
  reset() {
    sessionStorage.setItem(GET_TOKEN_FAILURE_COUNT_KEY, "0");
  }
};
const getKmsiFailureCount = {
  get() {
    const counterValue = sessionStorage.getItem(GET_KMSI_FAILURE_COUNT_KEY) || "";
    return Number.parseInt(counterValue) || 0;
  },
  increment() {
    const currentValue = this.get();
    sessionStorage.setItem(
      GET_KMSI_FAILURE_COUNT_KEY,
      (currentValue + 1).toString()
    );
  },
  reset() {
    sessionStorage.setItem(GET_KMSI_FAILURE_COUNT_KEY, "0");
  }
};
const decodeToken = (token) => {
  return jwtDecode(token);
};
const isTokenExpired = (decodedToken) => {
  const expiry = decodedToken.exp * 1e3;
  const currentTime = Date.now();
  return currentTime >= expiry;
};
async function fetchToken() {
  const tokenUrl = "/gettoken";
  const response = await fetch(tokenUrl, {
    credentials: "include",
    method: "GET"
  });
  if (response.status !== 200) {
    throw new Error(`Error fetching token: ${response.statusText}`);
  }
  const data = await response.json();
  if (!data.has_logged_in) {
    return null;
  }
  const idToken = data.id_token;
  if (!idToken) {
    throw new Error("Missing id_token in response data");
  }
  return idToken;
}
async function fetchKmsi() {
  const keepAliveUrl = "/kmsi";
  const response = await fetch(keepAliveUrl, {
    credentials: "include",
    method: "GET"
  });
  if (response.status !== 200) {
    throw new Error(
      `Request failed to /kmsi with status ${response.status}: ${response.statusText}`
    );
  }
  const data = await response.json();
  return data;
}
async function getToken() {
  try {
    const hashedUserId = Cookie.get("hashedUserId");
    const cachedToken = sessionStorage.getItem(
      `${AUTH_TOKEN_KEY}_${hashedUserId}`
    );
    if (!hashedUserId) {
      return { expired: false, hasLoggedIn: false };
    }
    if (cachedToken) {
      const decodedToken2 = decodeToken(cachedToken);
      if (!isTokenExpired(decodedToken2)) {
        return {
          expired: false,
          hasLoggedIn: true,
          token: cachedToken
        };
      }
    }
    if (getTokenFailureCount.get() > GET_TOKEN_FAILURE_MAX_RETRY_COUNT) {
      return { expired: true, hasLoggedIn: false };
    }
    const idToken = await fetchToken();
    if (!idToken) {
      return { hasLoggedIn: false };
    }
    const decodedToken = decodeToken(idToken);
    if (isTokenExpired(decodedToken)) {
      return { expired: true, hasLoggedIn: false };
    }
    getTokenFailureCount.reset();
    sessionStorage.setItem(
      `${AUTH_TOKEN_KEY}_${Cookie.get("hashedUserId")}`,
      idToken
    );
    return { expired: false, hasLoggedIn: true, token: idToken };
  } catch (error) {
    console.error("Error fetching or processing token:", error);
    sessionStorage.removeItem(
      `${AUTH_TOKEN_KEY}_${Cookie.get("hashedUserId")}`
    );
    getTokenFailureCount.increment();
    return { expired: true, hasLoggedIn: false };
  }
}
async function isWcsSessionEstablished() {
  try {
    const wcsSession = Cookie.get("hashedUserId");
    return !!wcsSession;
  } catch (error) {
    console.error("Error checking WCS session:", error);
  }
}
function isKmsiSession() {
  try {
    const validCookieFormat = new RegExp(/^(true_\d+|false)$/);
    const kmsiSession = Cookie.get("kmsi") || "";
    const isValidFormat = validCookieFormat.test(kmsiSession);
    const [isKmsiEnabledString] = (kmsiSession == null ? void 0 : kmsiSession.split("_")) ?? [];
    return !!kmsiSession && isValidFormat && isKmsiEnabledString === "true";
  } catch (error) {
    console.error("Error checking if KMSI is enabled:", error);
  }
}
async function kmsi() {
  try {
    const isWcsSession = await isWcsSessionEstablished();
    if (isWcsSession) {
      if (getKmsiFailureCount.get() >= GET_KMSI_FAILURE_MAX_RETRY_COUNT) {
        console.error(
          "KMSI failed to enable after 3 attempts, redirecting to logon page"
        );
        window.location.href = "/LogonForm?prompt=false&fromPage=/";
        return;
      }
      const response = await fetchKmsi();
      if (response.kmsi === "true") {
        getKmsiFailureCount.reset();
      } else {
        console.error("KMSI failed, retrying...");
        getKmsiFailureCount.increment();
        return kmsi();
      }
    }
  } catch (error) {
    console.log(error);
    window.location.href = "/LogonForm?prompt=false&fromPage=/";
  }
}
export {
  decodeToken,
  getToken,
  isKmsiSession,
  isTokenExpired,
  isWcsSessionEstablished,
  kmsi
};
